import React, { useEffect, useState } from "react";
import {
	Disclosure,
	DisclosureButton,
	DisclosurePanel,
} from "@headlessui/react";
import {
	Bars3Icon,
	ChevronDownIcon,
	XMarkIcon,
} from "@heroicons/react/24/outline";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import logoJMD from "../../assets/img/logo-jmd.png";
import logokemenag from "../../assets/img/logo-kemenang.png";
import axios from "axios";
import { toast } from "react-toastify";

const navigation = [
	{ name: "Beranda", href: "/", current: false },
	{ name: "Berita", href: "/berita", current: false },
	{ name: "Artikel", href: "/artikel", current: false },
	{
		name: "Profil",
		href: "",
		current: false,
		submenu: [
			{ name: "Visi Misi", href: "/visi-misi" },
			{ name: "Kontak", href: "/kontak" },
			{ name: "Sambutan", href: "/sambutan" },
		],
	},
	{
		name: "Galeri",
		href: "",
		current: false,
		submenu: [
			{ name: "Foto", href: "/galeri-foto" },
			{ name: "Video", href: "/galeri-video" },
		],
	},
	{
		name: "PPDB",
		href: "https://pmbm.jogjamadrasahdigital.net/",
		current: false,
	},
	{ name: "Fitur", href: "/feature", current: false },
];

function classNames(...classes) {
	return classes.filter(Boolean).join(" ");
}

export default function Header({ isInsideHero = false, className = "" }) {
	const location = useLocation();
	const [logos, setLogos] = useState([]);
	const [isScrolled, setIsScrolled] = useState(false);

	const updatedNavigation = navigation.map((item) => ({
		...item,
		current: item.href === location.pathname,
	}));

	// Scroll effect
	useEffect(() => {
		const handleScroll = () => {
			setIsScrolled(window.scrollY > 20);
		};
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	// Scroll effect
	useEffect(() => {
		const fetchLogoData = async () => {
			try {
				const response = await axios.get("/api_w/logo", {
					headers: {
						db_name: process.env.REACT_APP_DB_NAME,
					},
				});
				const logoData = response.data.data || [];

				// Default logo
				const defaultLogos = [
					{ id: 1, url: logoJMD },
					{ id: 2, url: logokemenag },
				];

				// Check if the API response contains logos with ID 1 and 2
				const logosDefaults = defaultLogos.map((defaultLogo) => {
					const dataLogo = logoData.find((logo) => logo.id === defaultLogo.id);
					return dataLogo || defaultLogo;
				});

				setLogos(logosDefaults);
			} catch (error) {
				toast.error(error.response?.data?.message || error.message);
				setLogos([
					{ id: 1, url: logoJMD },
					{ id: 2, url: logokemenag },
				]);
			}
		};

		fetchLogoData();
	}, []);

	const isHomePage = location.pathname === "/";

	return (
		<div>
			<Disclosure
				as="nav"
				className={`${isInsideHero && !isScrolled ? "absolute" : "fixed"} top-0 w-full z-50 transition-all duration-500 ${
					isScrolled
						? "bg-green-800/95 backdrop-blur-md shadow-lg"
						: !isHomePage
							? "bg-green-800"
							: "bg-transparent"
				} ${className}`}
			>
				{({ open }) => (
					<div className="mx-auto">
						<div className="relative flex h-16 items-center justify-between">
							{/* Logo Tampilan Mobile */}
							<div className="absolute inset-y-0 left-3 flex items-center sm:hidden">
								<div className="flex sm:hidden flex-1 justify-start">
									{logos.map((logo) => (
										<Link to="/" key={logo.id}>
											<img
												className="h-10 w-auto mr-1.5 drop-shadow-md transition-transform duration-300 hover:scale-110 rounded-full"
												src={logo.url}
												alt="Logo"
												onError={(e) => {
													e.target.src = logo.id === 1 ? logoJMD : logokemenag;
												}}
											/>
										</Link>
									))}
								</div>
							</div>
							{/* Logo Tampilan Desktop */}
							<div className="hidden sm:flex flex-shrink-0 items-center pl-3">
								{logos.map((logo) => (
									<Link to="/" key={logo.id}>
										<img
											className="h-12 w-auto mr-2 drop-shadow-md transition-transform duration-300 hover:scale-110 rounded-full"
											src={logo.url}
											alt="Logo"
											onError={(e) => {
												e.target.src = logo.id === 1 ? logoJMD : logokemenag;
											}}
										/>
									</Link>
								))}
							</div>
							{/* Menu Tampilan Desktop */}
							<div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-center">
								<div className="hidden sm:ml-6 sm:block">
									<div className="flex space-x-1">
										{updatedNavigation.map((item) => (
											<div className="group relative" key={item.name}>
												<Link
													to={item.href}
													className={classNames(
														item.current
															? "text-white bg-white/15 rounded-lg"
															: "text-green-100 hover:text-white hover:bg-white/10 rounded-lg",
														"text-sm flex items-center px-3 py-2 font-medium transition-all duration-300"
													)}
													aria-current={item.current ? "page" : undefined}
												>
													{item.name}
													{item.submenu && (
														<ChevronDownIcon className="ml-1 h-3.5 w-3.5 text-green-200 transition-transform group-hover:rotate-180 duration-300" />
													)}
												</Link>
												{item.submenu && (
													<div className="absolute left-0 top-full pt-1 hidden group-hover:block z-50">
														<div className="bg-white rounded-xl shadow-xl shadow-green-900/10 border border-green-100 overflow-hidden min-w-[180px]">
															{item.submenu.map((subItem) => (
																<Link
																	key={subItem.name}
																	to={subItem.href}
																	className="text-gray-700 block px-4 py-2.5 text-sm hover:bg-green-50 hover:text-green-700 transition-colors duration-200"
																>
																	{subItem.name}
																</Link>
															))}
														</div>
													</div>
												)}
											</div>
										))}
									</div>
								</div>
							</div>
							<div className="absolute inset-y-0 right-0 flex items-center pr-3 sm:static sm:inset-auto sm:ml-6">
								{/* Hubungi Kami Button - Desktop */}
								<Link
									to="/kontak"
									className="hidden sm:flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 shadow-lg shadow-green-900/20 transition-all duration-300 hover:shadow-xl hover:scale-105"
								>
									Hubungi Kami
								</Link>
								{/* Icon Hamburger Tampilan Mobile */}
								<DisclosureButton className="sm:hidden relative inline-flex items-center justify-center rounded-lg p-2 text-white hover:bg-white/10 transition-colors duration-300">
									<span className="sr-only">Open main menu</span>
									{open ? (
										<XMarkIcon className="block h-7 w-7" aria-hidden="true" />
									) : (
										<Bars3Icon className="block h-7 w-7" aria-hidden="true" />
									)}
								</DisclosureButton>
							</div>
						</div>
						{/* Menu Tampilan Mobile */}
						<DisclosurePanel className="sm:hidden absolute top-16 inset-x-0 bg-white/95 backdrop-blur-md z-50 shadow-xl rounded-b-2xl border-t border-green-100">
							<div className="flex flex-col space-y-1 p-3">
								{updatedNavigation.map((item) => (
									<div className="group relative" key={item.name}>
										<Link
											to={item.href}
											className={classNames(
												item.current
													? "text-green-700 bg-green-50 rounded-lg"
													: "text-gray-700 hover:bg-green-50 hover:text-green-700 rounded-lg",
												"text-sm flex items-center px-4 py-2.5 font-medium transition-colors duration-200"
											)}
											aria-current={item.current ? "page" : undefined}
										>
											{item.name}
											{item.submenu && (
												<ChevronDownIcon className="ml-1 h-4 w-4 text-gray-400" />
											)}
										</Link>
										{item.submenu && (
											<div className="absolute left-24 top-0 mt-0 hidden bg-white shadow-xl rounded-xl group-hover:block border border-green-100 z-50 min-w-[160px]">
												{item.submenu.map((subItem) => (
													<Link
														key={subItem.name}
														to={subItem.href}
														className="text-gray-600 block px-4 py-2.5 text-sm hover:bg-green-50 hover:text-green-700 transition-colors duration-200"
													>
														{subItem.name}
													</Link>
												))}
											</div>
										)}
									</div>
								))}
								<div className="pt-2 border-t border-gray-100">
									{/* Hubungi Kami Button - Mobile */}
									<Link
										to="/kontak"
										className="flex items-center justify-center gap-2 mx-2 py-2.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors duration-200"
									>
										<svg
											xmlns="http://www.w3.org/2000/svg"
											className="h-4 w-4"
											viewBox="0 0 20 20"
											fill="currentColor"
										>
											<path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
											<path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
										</svg>
										Hubungi Kami
									</Link>
								</div>
							</div>
						</DisclosurePanel>
					</div>
				)}
			</Disclosure>
		</div>
	);
}
